import {
  BlockMapBuilder,
  CharacterMetadata,
  ContentBlock,
  EditorState,
  Modifier,
  convertToRaw,
  type RawDraftContentState,
} from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";

/**
 * SCRUM-1115 — estilos heredados al pegar texto en las opciones de un quiz.
 *
 * Al pegar desde Word / Google Docs / una web, html-to-draftjs conserva el
 * color, fondo, tamaño y fuente del origen como estilos inline de draft-js
 * (`color-…`, `bgcolor-…`, `fontsize-…`, `fontfamily-…`). El alumno nunca los
 * ve (el quiz-engine descarta `style` en web y en nativo), pero el
 * `fontfamily-` rompía el HTML guardado: html-to-draftjs recorta SOLO la
 * primera y la última comilla de la lista de fuentes, así que
 * `"Courier New", Courier` queda como `Courier New", Courier` y
 * draftjs-to-html lo serializa con una comilla de menos:
 *
 *   <span style="…;font-family: Courier New", Courier, monospace;">texto</span>
 *
 * El parser nativo del engine (hasta alpha.67) contaba comillas de forma
 * ingenua, nunca encontraba el `>` y la opción salía VACÍA en la app (quiz 318
 * pregunta 4, quiz 259 pregunta 3). Por eso:
 *
 *   1. `handlePastedHtml` inserta lo pegado SIN sus estilos de presentación:
 *      queda el texto con negrita / cursiva / subrayado, y el autor aplica
 *      color o tamaño con la barra si los quiere (así el pegado desde un doc
 *      en modo oscuro tampoco queda blanco sobre blanco en el editor).
 *   2. `editorStateToHtml` nunca emite `font-family` ni `background-color`:
 *      ningún botón de la barra los produce, así que solo pueden venir
 *      heredados (por ejemplo, de contenido viejo ya guardado con ellos).
 */

/** Estilos de presentación que trae un pegado con formato. */
const PASTED_PRESENTATION_PREFIXES = [
  "color-",
  "bgcolor-",
  "fontsize-",
  "fontfamily-",
];

/** Estilos que ningún botón de la barra produce: solo pueden venir heredados. */
const INHERITED_STYLE_PREFIXES = ["fontfamily-", "bgcolor-"];

const startsWithAny = (style: string, prefixes: string[]): boolean =>
  prefixes.some((prefix) => style.startsWith(prefix));

/** Quita color, fondo, tamaño y fuente de los bloques que vienen de un pegado. */
export function stripPastedPresentation(
  blocks: ContentBlock[],
): ContentBlock[] {
  return blocks.map((block) => {
    let changed = false;
    const characterList = block.getCharacterList().map((char) => {
      let next = char as CharacterMetadata;
      next.getStyle().forEach((style) => {
        if (style && startsWithAny(style, PASTED_PRESENTATION_PREFIXES)) {
          next = CharacterMetadata.removeStyle(next, style);
          changed = true;
        }
      });
      return next;
    });
    if (!changed) return block;
    return new ContentBlock({
      key: block.getKey(),
      type: block.getType(),
      text: block.getText(),
      characterList,
      depth: block.getDepth(),
      data: block.getData(),
    });
  });
}

/**
 * Reemplazo del pegado por defecto de react-draft-wysiwyg (mismo flujo:
 * html-to-draftjs → `replaceWithFragment`), pero sin los estilos heredados.
 * Devuelve `false` para un pegado sin HTML, que draft-js maneja como texto.
 */
export function handlePastedHtml(
  html: string | undefined,
  editorState: EditorState,
  onChange: (state: EditorState) => void,
): boolean {
  if (!html) return false;
  const converted = htmlToDraft(html);
  if (!converted) return false;

  let content = editorState.getCurrentContent();
  converted.entityMap?.forEach((value: unknown, key: string) => {
    content = content.mergeEntityData(key, value as Record<string, unknown>);
  });
  content = Modifier.replaceWithFragment(
    content,
    editorState.getSelection(),
    BlockMapBuilder.createFromArray(
      stripPastedPresentation(converted.contentBlocks),
    ),
  );
  onChange(EditorState.push(editorState, content, "insert-characters"));
  return true;
}

/** Quita del contenido crudo los estilos que solo pueden venir heredados. */
export function stripInheritedInlineStyles(
  raw: RawDraftContentState,
): RawDraftContentState {
  return {
    ...raw,
    blocks: raw.blocks.map((block) => ({
      ...block,
      inlineStyleRanges: block.inlineStyleRanges.filter(
        (range) => !startsWithAny(range.style, INHERITED_STYLE_PREFIXES),
      ),
    })),
  };
}

export function editorStateToHtml(state: EditorState): string {
  const raw = stripInheritedInlineStyles(
    convertToRaw(state.getCurrentContent()),
  );
  const html = draftToHtml(raw);
  return html.trim() === "<p></p>" ? "" : html;
}
